import { useCallback } from 'react';
import { useGetRecentlyViewedGoods } from './useGetRecentlyViewedGoods';
import type { FlatListProps } from 'react-native';
import { bridge, getNumberRange } from 'shared/src/utils';
import type { InfiniteRecentlyViewedGoods } from '../jotai/infiniteRecentlyViewedGood.atom';
import type { GoodsData } from '@banhala/ui-kit';
import { SCREEN_NAME } from '../constants';
import {
  retainedStateFamily,
  useRetainedRef,
} from 'shared/src/hooks/retainedState';

const loggedIndicesFamily = retainedStateFamily<string, number[]>(() => []);

export const useLogDisplayGoods = () => {
  const { recentlyViewedGoods } = useGetRecentlyViewedGoods();

  const goodsItemLoggedList = useRetainedRef(
    loggedIndicesFamily('recently-viewed-goods'),
  );
  const logDisplayGoods = useCallback<
    NonNullable<
      FlatListProps<
        GoodsData & InfiniteRecentlyViewedGoods['goods'][number]
      >['onViewableItemsChanged']
    >
  >(
    ({ viewableItems }) => {
      viewableItems.forEach(({ index, isViewable, item }) => {
        if (index === null || !isViewable) {
          return;
        }
        if (goodsItemLoggedList.current.includes(index)) {
          return;
        }
        goodsItemLoggedList.current.push(index);

        bridge.logAnalyticsEvent({
          channel: ['amplitude', 'ablylog', 'firebase'],
          name: 'DISPLAY_GOODS',
          analytics: {
            SCREEN: SCREEN_NAME,
            CATEGORY_DEPTH: item.category?.depth,
            CATEGORY_NAME: item.category?.name,
            CATEGORY_SNO: item.category?.sno.toString(),
            DELIVERY_TYPE: item.delivery_type,
            DISCOUNT_RATE: item.discount_rate,
            GOODS_NAME: item.name,
            GOODS_SNO: item.sno.toString(),
            IMAGE_BADGE_LIST: item.render.data.image_badge_list.map(
              ({ type }) => type,
            ),
            IS_AD: item.ad !== null ? '1' : '0',
            IS_NEW: item.is_new ? '1' : '0',
            // item.likes_count가 있으면 item.likes_count를 사용해야한다.
            // 해당 페이지는 item.likes_count가 없어서 null로 처리
            LIKES_COUNT: null,
            // item.likes_count로 500 단위로 끊어서 전송 (2,001~2,500)
            // 해당 페이지는 item.likes_count가 없어서 null로 처리
            LIKES_COUNT_RANGE: null,
            LIST_ID: recentlyViewedGoods.list_id,
            LIST_INDEX: index,
            LIST_PARAMS: recentlyViewedGoods.list_params,
            MARKET_NAME: item.market.name,
            MARKET_SNO: item.market.sno.toString(),
            MARKET_TYPE: item.market.market_type.name,
            ORIGINAL_PRICE:
              item.first_page_rendering?.original_price ??
              item.linked_option.original_price,
            REVIEW_COUNT: item.total_review_count,
            REVIEW_COUNT_RANGE: getNumberRange({
              target: item.total_review_count,
              rangeUnit: 50,
            }),
            REVIEW_RATING: item.positive_review_rate,
            SALES_PRICE: item.price,
            SALES_PRICE_RANGE: getNumberRange({
              target: item.price,
              rangeUnit: 5000,
            }),
            STANDARD_CATEGORY_NAME: item.standard_category.name,
            STANDARD_CATEGORY_SNO: item.standard_category.sno.toString(),
            MARKET_TYPE_SNO: item.market.market_type.sno.toString(),
            INDEX: index,
          },
        });
      });
    },
    [
      goodsItemLoggedList,
      recentlyViewedGoods.list_id,
      recentlyViewedGoods.list_params,
    ],
  );

  return { logDisplayGoods };
};
